import axios from 'axios';
import { copyFinder } from '../models/legacyFinderDraft';

let stocksRequest = null;
let stocksRevision = 0;
const cacheLifetime = 5 * 60 * 1000;
let resources = null;
const resourceIdentity = scope => JSON.stringify([String(scope || ''), localStorage.getItem('user_access_token')]);

function accountResources(scope) {
  const identity = resourceIdentity(scope);
  if (!resources || resources.identity !== identity) resources = { identity, finder: {}, options: {} };
  return resources;
}

function cachedValue(entry) {
  return entry.value && Date.now() < entry.expires ? copyFinder(entry.value) : null;
}

function readResource(key, scope, request) {
  const account = accountResources(scope), entry = account[key];
  const cached = cachedValue(entry);
  if (cached) return Promise.resolve(cached);
  if (!entry.task) {
    const task = request().then(value => {
      if (resources !== account || account[key] !== entry || resourceIdentity(scope) !== account.identity) {
        const error = new Error('Filtros sustituidos'); error.code = 'STALE_QUERY'; throw error;
      }
      entry.value = copyFinder(value); entry.expires = Date.now() + cacheLifetime;
      return value;
    }).finally(() => { if (entry.task === task) entry.task = null; });
    entry.task = task;
  }
  return entry.task.then(copyFinder);
}

export function invalidateLegacyFinder() {
  if (resources) resources.finder = {};
}

export function peekLegacyFinder(scope = '') { return cachedValue(accountResources(scope).finder); }
export function peekLegacyFinderOptions(scope = '') { return cachedValue(accountResources(scope).options); }

const client = axios.create({ baseURL: process.env.VUE_APP_API || '', withCredentials: true, timeout: 60000 });
client.interceptors.request.use(config => {
  const token = localStorage.getItem('user_access_token');
  if (token) config.headers.authorization = `Bearer ${token}`;
  return config;
});

export function loadLegacyFinder(defaults = false, scope = '') {
  const request = async () => {
    const { data } = await client.get('/api/v1/stock-finder/default-finder', { params: defaults ? { defaults: 1 } : {} });
    if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Respuesta de filtros inválida');
    return data;
  };
  // Reset is a preview, never the next applied finder.
  return defaults ? request() : readResource('finder', scope, request);
}

export function loadLegacyFinderOptions(scope = '') {
  return readResource('options', scope, async () => {
    const values = await Promise.all(['currencies', 'countries', 'industries'].map(async name => {
      const { data } = await client.get(`/api/v1/stock-finder/available-${name}`);
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Opciones de filtros inválidas');
      return data;
    }));
    return { currencies: values[0], countries: values[1], industries: values[2] };
  });
}

export function prefetchLegacyFinder(scope = '') {
  return Promise.all([loadLegacyFinder(false, scope), loadLegacyFinderOptions(scope)]);
}

export async function saveLegacyFinder(finder, crypto, scope = '') {
  const account = accountResources(scope), snapshot = copyFinder(finder);
  invalidateLegacyFinder();
  const entry = account.finder;
  const params = new URLSearchParams();
  params.append('finder', JSON.stringify(snapshot));
  params.append('search_mode', crypto ? 'crypto' : 'stock_market');
  await client.post('/api/v1/stock-finder/update-last-search', params);
  if (resources === account && resourceIdentity(scope) === account.identity && account.finder === entry) {
    account.finder = { value: snapshot, expires: Date.now() + cacheLifetime };
  }
  ++stocksRevision;
  stocksRequest = null;
}

export async function saveLegacyNamedFinder(name, finder) {
  const params = new URLSearchParams();
  params.append('name', name.trim().replace(/\s+/g, 'Q1SPACEQ1'));
  params.append('finder', JSON.stringify(finder));
  await client.post('/api/v1/stock-finder/create-finder', params);
}

export function loadLegacyFinderStocks(scope = '', compact = false) {
  const identity = JSON.stringify([localStorage.getItem('user_access_token'), scope, compact]);
  if (stocksRequest && stocksRequest.identity === identity) return stocksRequest.task;
  const revision = stocksRevision;
  const task = client.get('/api/v1/stock-finder/stocks', { params: compact ? { view: 'legacy-chart' } : {} }).then(({ data }) => {
    if (revision !== stocksRevision) { const stale = new Error('Búsqueda sustituida'); stale.code = 'STALE_QUERY'; throw stale; }
    if (!Array.isArray(data)) throw new Error('Respuesta de valores inválida');
    return data;
  }).catch(error => {
    if (revision !== stocksRevision) { error.code = 'STALE_QUERY'; throw error; }
    throw error;
  }).finally(() => { if (stocksRequest && stocksRequest.task === task) stocksRequest = null; });
  stocksRequest = { identity, task };
  return task;
}

export async function loadLegacyFavoriteFolderCounts(folders, onCounts, isCurrent = () => true) {
  let next = 0;
  async function worker() {
    while (isCurrent() && next < folders.length) {
      const folder = folders[next++];
      if (folder.count === 0) continue;
      let counts = null;
      try {
        const { data } = await client.get('/api/v1/stock-finder/stocks/favoritos/' + encodeURIComponent(folder.name), { params: { view: 'collection-counts' } });
        if (data && Number.isInteger(data.entry_count) && data.entry_count >= 0 &&
            Number.isInteger(data.ticker_count) && data.ticker_count >= 0 && data.ticker_count <= data.entry_count) counts = data;
      } catch (_) { /* The dialog keeps an explicit unavailable count. */ }
      if (isCurrent()) onCounts(folder, counts);
    }
  }
  await Promise.all(Array.from({ length: Math.min(3, folders.length) }, worker));
}
